/**
 * Shared axios client for the Admin app.
 * Same pattern as web-store but points to admin-specific token storage.
 */

import type { ApiResponse } from '@burmazon/shared';
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

export const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

let accessToken: string | null = null;

// Subscribers are notified whenever the access token changes, enabling
// reactive consumers (e.g. socket hooks) to reconnect automatically.
type TokenSubscriber = () => void;
const tokenSubscribers = new Set<TokenSubscriber>();

export const setAccessToken = (t: string | null): void => {
  accessToken = t;
  tokenSubscribers.forEach((fn) => fn());
};

export const getAccessToken = (): string | null => accessToken;

export const subscribeToToken = (fn: TokenSubscriber): (() => void) => {
  tokenSubscribers.add(fn);
  return () => tokenSubscribers.delete(fn);
};

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

// ── Token refresh + retry ──────────────────────────────────────────────────────
let isRefreshing = false;
type QueueItem = { resolve: (token: string) => void; reject: (err: unknown) => void };
let failedQueue: QueueItem[] = [];

const processQueue = (error: unknown, token: string | null) => {
  failedQueue.forEach((item) => (error ? item.reject(error) : item.resolve(token!)));
  failedQueue = [];
};

function extractError(error: AxiosError): Error {
  const serverMessage = (error.response?.data as { error?: { message?: string } })?.error?.message;
  if (serverMessage) return new Error(serverMessage);

  if (!error.response) {
    if (error.code === 'ECONNABORTED' || error.message.toLowerCase().includes('timeout')) {
      return new Error('Request timed out — please check your connection and try again');
    }
    return new Error('Unable to reach the server — please check your network connection');
  }

  const fallbacks: Record<number, string> = {
    400: 'Invalid request — please check your input',
    401: 'Session expired — please log in again',
    403: 'You do not have permission to perform this action',
    404: 'The requested resource was not found',
    409: 'This action conflicts with existing data',
    422: 'Validation failed — please check your input',
    429: 'Too many requests — please wait a moment and try again',
    500: 'A server error occurred — please try again later',
    502: 'Service temporarily unavailable — please try again',
    503: 'Service temporarily unavailable — please try again',
  };
  const status = error.response.status;
  return new Error(fallbacks[status] ?? `Request failed (${status})`);
}

apiClient.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(extractError(error));
    }

    // Never retry auth endpoints — login 401 means bad credentials, not expired token
    if (original.url?.includes('/auth/')) {
      return Promise.reject(extractError(error));
    }

    // Another request is already refreshing — queue this one
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        original.headers.Authorization = `Bearer ${token}`;
        return apiClient(original);
      });
    }

    original._retry = true;
    isRefreshing = true;

    const refreshToken = localStorage.getItem('admin-refreshToken');
    if (!refreshToken) {
      isRefreshing = false;
      setAccessToken(null);
      window.location.href = '/login';
      return Promise.reject(extractError(error));
    }

    try {
      const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken });
      const { accessToken: newToken, refreshToken: newRefresh } = data.data.tokens;
      setAccessToken(newToken);
      localStorage.setItem('admin-refreshToken', newRefresh);
      processQueue(null, newToken);
      original.headers.Authorization = `Bearer ${newToken}`;
      return apiClient(original);
    } catch (refreshErr) {
      const err =
        refreshErr instanceof Error
          ? refreshErr
          : new Error('Session expired — please log in again');
      processQueue(err, null);
      setAccessToken(null);
      localStorage.removeItem('admin-refreshToken');
      window.location.href = '/login';
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  },
);

export async function apiGet<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const res = await apiClient.get<ApiResponse<T>>(url, { params });
  if (!res.data.success) throw new Error(res.data.error.message);
  return res.data.data;
}

export async function apiPost<T>(url: string, data?: unknown): Promise<T> {
  const res = await apiClient.post<ApiResponse<T>>(url, data);
  if (!res.data.success) throw new Error(res.data.error.message);
  return res.data.data;
}

export async function apiPatch<T>(url: string, data?: unknown): Promise<T> {
  const res = await apiClient.patch<ApiResponse<T>>(url, data);
  if (!res.data.success) throw new Error(res.data.error.message);
  return res.data.data;
}

export async function apiPut<T>(url: string, data?: unknown): Promise<T> {
  const res = await apiClient.put<ApiResponse<T>>(url, data);
  if (!res.data.success) throw new Error(res.data.error.message);
  return res.data.data;
}

export async function apiDelete(url: string): Promise<void> {
  await apiClient.delete(url);
}
